import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArchiveTrayIcon, UploadSimple, Play, ArrowCounterClockwise, Check, X, PencilSimple, FunnelSimple, FilePdf, Vault } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import FileDropField from '@/components/ui/FileDropField';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, isAdmin } from '@/auth/AuthContext';
import { plannerApi } from './plannerApi';
import { ArchivedContextCard, CONF_BADGE, STATUS_BADGE } from './components/ArchivedContextSection';
import CreateOldProjectDialog from './components/CreateOldProjectDialog';
import { formatJST } from '@/utils/datetime';

const ALL = '__all__';
const NONE = '__none__';

export default function ArchiveReview() {
  const { t } = useT();
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [pickedFile, setPickedFile] = useState(null);
  const [records, setRecords] = useState([]);
  const [sourceFiles, setSourceFiles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [batches, setBatches] = useState([]);
  const [fConf, setFConf] = useState(ALL);
  const [fStatus, setFStatus] = useState('pending_review');
  const [fRel, setFRel] = useState(ALL);
  const [fFile, setFFile] = useState(ALL);
  const [selected, setSelected] = useState({});
  const [dryRun, setDryRun] = useState(null);
  const [pendingSource, setPendingSource] = useState(null); // { file } or { source: 'bundled_pdf' }
  const [autoApprove, setAutoApprove] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editRec, setEditRec] = useState(null);
  const [editCand, setEditCand] = useState(NONE);
  const [editRel, setEditRel] = useState('historical_context');
  const [editNote, setEditNote] = useState('');
  const [oldProjRec, setOldProjRec] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (fConf !== ALL) params.confidence = fConf;
    if (fStatus !== ALL) params.review_status = fStatus;
    if (fRel !== ALL) params.relationship_type = fRel;
    if (fFile !== ALL) params.source_file = fFile;
    plannerApi.archiveReview(params).then((d) => { setRecords(d.records || []); setSourceFiles(d.source_files || []); }).catch(() => {});
  }, [fConf, fStatus, fRel, fFile]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    plannerApi.projects({ include_archived: true }).then(setProjects).catch(() => {});
    if (admin) plannerApi.archiveBatches().then(setBatches).catch(() => {});
  }, [admin]);

  const projName = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p.name])), [projects]);

  const buildForm = (src) => {
    const fd = new FormData();
    if (src.file) fd.append('file', src.file);
    if (src.source) fd.append('source', src.source);
    return fd;
  };

  const runDryRun = async (src) => {
    setBusy(true);
    try {
      const res = await plannerApi.archiveDryRun(buildForm(src));
      setDryRun(res); setPendingSource(src);
    } catch (e) { toast.error(e?.response?.data?.detail || t('planner.error')); }
    setBusy(false);
  };

  const runCommit = async () => {
    if (!pendingSource) return;
    setBusy(true);
    try {
      const fd = buildForm(pendingSource);
      fd.append('auto_approve_exact', autoApprove ? 'true' : 'false');
      const res = await plannerApi.archiveCommit(fd);
      toast.success(`${res.imported}件をインポートしました（自動承認: ${res.auto_approved}件）`);
      setDryRun(null); setPendingSource(null); setAutoApprove(false);
      load(); plannerApi.archiveBatches().then(setBatches).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || t('planner.error')); }
    setBusy(false);
  };

  const runRollback = async (batchId) => {
    if (!window.confirm('このバッチでインポートした過去関連作業レコードのみ削除します。現在のプロジェクトには影響しません。よろしいですか？')) return;
    try {
      const res = await plannerApi.archiveRollback(batchId);
      toast.success(`${res.deleted}件を削除しました`);
      load(); plannerApi.archiveBatches().then(setBatches).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || t('planner.error')); }
  };

  const doApprove = async (rec) => {
    try { await plannerApi.archiveApprove(rec.id); toast.success(t('planner.saved')); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('planner.error')); }
  };
  const doReject = async (rec) => {
    try { await plannerApi.archiveReject(rec.id); toast.success(t('planner.saved')); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('planner.error')); }
  };
  const openEdit = (rec) => {
    setEditRec(rec);
    setEditCand(rec.candidate_current_project_id || NONE);
    setEditRel(rec.relationship_type || 'historical_context');
    setEditNote(rec.reviewer_note || '');
  };
  const saveEdit = async () => {
    try {
      await plannerApi.archivePatch(editRec.id, {
        candidate_current_project_id: editCand === NONE ? null : editCand,
        relationship_type: editRel, reviewer_note: editNote,
      });
      toast.success(t('planner.saved')); setEditRec(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('planner.error')); }
  };

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const bulkApprove = async () => {
    if (selectedIds.length === 0) return;
    try {
      const res = await plannerApi.archiveBulkApprove(selectedIds);
      toast.success(`${res.approved}件を承認しました（スキップ: ${res.skipped}件）`);
      setSelected({}); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('planner.error')); }
  };

  const canCreateOld = (rec) => rec.review_status !== 'approved_link' && rec.review_status !== 'old_project_created' && !rec.old_project_id;

  const CONFS = ['high', 'medium', 'low', 'unmatched'];
  const STATUSES = ['pending_review', 'approved_link', 'rejected_link', 'needs_more_info', 'old_project_created'];
  const RELS = ['historical_context', 'predecessor_project', 'related_subtask', 'similar_equipment', 'completed_legacy_project', 'work_log_reference', 'unmatched_archive'];

  return (
    <div className="space-y-5" data-testid="archive-review-page">
      <div>
        <div className="flex items-center gap-2">
          <ArchiveTrayIcon size={22} weight="duotone" className="text-amber-600" />
          <h1 className="text-xl font-semibold tracking-tight text-zinc-900">{t('planner.archive.reviewTitle')}</h1>
        </div>
        <p className="text-xs text-zinc-500 mt-1">{t('planner.archive.subtitle')} — {t('planner.archive.referenceOnly')}</p>
      </div>

      {admin && (
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4 space-y-3" data-testid="archive-import-panel">
          <h2 className="text-sm font-bold text-zinc-800">{t('planner.archive.importHeading')}</h2>
          <div className="flex flex-wrap items-center gap-2">
            <FileDropField testid="archive-import-file" accept=".xlsx,.json" className="w-full sm:w-80"
              hint="Odoo Knowledge XLSX / JSON" onFiles={(f) => setPickedFile(f)} />
            <Button size="sm" variant="outline" disabled={busy} data-testid="archive-dryrun-btn"
              onClick={() => { if (!pickedFile) { toast.error(t('common.fileRequired')); return; } runDryRun({ file: pickedFile }); }}>
              <Play size={14} className="mr-1" /> {t('planner.archive.dryRun')}
            </Button>
            <Button size="sm" variant="outline" disabled={busy} data-testid="archive-dryrun-pdf-btn"
              onClick={() => runDryRun({ source: 'bundled_pdf' })} className="text-amber-700 border-amber-200 hover:bg-amber-50">
              <FilePdf size={14} className="mr-1" /> {t('planner.archive.dryRunPdf')}
            </Button>
          </div>
          {dryRun && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-2" data-testid="archive-dryrun-result">
              <p className="text-xs font-semibold text-zinc-700">
                試行結果: {dryRun.source_file_name} — 候補 <b data-testid="dryrun-total">{dryRun.total}</b> 件
                （高: {dryRun.counts.high} ／ 中: {dryRun.counts.medium} ／ 低: {dryRun.counts.low} ／ 未一致: {dryRun.counts.unmatched}）
                <span className="text-zinc-400 ml-1">※ 試行段階ではデータは書き込まれません</span>
              </p>
              <div className="max-h-56 overflow-y-auto space-y-1">
                {(dryRun.records || []).map((r, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-1.5 text-[11px] bg-white rounded px-2 py-1 border border-zinc-100">
                    <span className="font-medium text-zinc-700">{r.archived_project_name}</span>
                    <Badge variant="outline" className={`${CONF_BADGE[r.match_confidence]} text-[10px] px-1 py-0`}>{t(`planner.archive.conf.${r.match_confidence}`)}</Badge>
                    {r.candidate_project_name && <span className="text-indigo-600">→ {r.candidate_project_name}</span>}
                    <span className="text-zinc-400">{t(`planner.archive.rel.${r.relationship_type}`)}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-3 pt-1">
                <label className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
                  <input type="checkbox" checked={autoApprove} onChange={(e) => setAutoApprove(e.target.checked)} data-testid="archive-auto-approve-checkbox" />
                  {t('planner.archive.autoApproveExact')}
                </label>
                <Button size="sm" onClick={runCommit} disabled={busy} data-testid="archive-commit-btn" className="bg-teal-700 hover:bg-teal-800">
                  <UploadSimple size={14} className="mr-1" /> {t('planner.archive.commit')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setDryRun(null); setPendingSource(null); }}>{t('planner.cancel')}</Button>
              </div>
            </div>
          )}
          {batches.length > 0 && (
            <div className="pt-1" data-testid="archive-batches">
              <p className="text-[11px] font-bold text-zinc-500 uppercase tracking-wide mb-1">{t('planner.archive.importHistory')}</p>
              <div className="space-y-1">
                {batches.map((b) => (
                  <div key={b.id} className="flex flex-wrap items-center gap-2 text-[11px] text-zinc-600 bg-zinc-50 rounded px-2 py-1.5">
                    <span className="font-medium">{b.source_file_name}</span>
                    <span>{b.record_count}件</span>
                    <span className="text-zinc-400">{formatJST(b.imported_at)} ・ {b.imported_by}</span>
                    {b.rolled_back
                      ? <Badge variant="outline" className="bg-zinc-100 text-zinc-400 border-zinc-200 text-[10px] px-1.5 py-0">{t('planner.archive.rolledBack')}</Badge>
                      : <button onClick={() => runRollback(b.id)} data-testid={`archive-rollback-${b.id}`}
                          className="ml-auto inline-flex items-center gap-1 text-rose-600 hover:text-rose-700 font-medium">
                          <ArrowCounterClockwise size={12} /> {t('planner.archive.rollback')}
                        </button>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2" data-testid="archive-review-filters">
        <FunnelSimple size={16} className="text-zinc-400" />
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="h-8 w-auto min-w-[130px] text-xs" data-testid="filter-review-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>{t('planner.archive.reviewStatus')}: 全て</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`planner.archive.status.${s}`)}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fConf} onValueChange={setFConf}>
          <SelectTrigger className="h-8 w-auto min-w-[110px] text-xs" data-testid="filter-confidence"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>{t('planner.archive.confidence')}: 全て</SelectItem>{CONFS.map((c) => <SelectItem key={c} value={c}>{t(`planner.archive.conf.${c}`)}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fRel} onValueChange={setFRel}>
          <SelectTrigger className="h-8 w-auto min-w-[130px] text-xs" data-testid="filter-relationship"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>{t('planner.archive.relationshipType')}: 全て</SelectItem>{RELS.map((r) => <SelectItem key={r} value={r}>{t(`planner.archive.rel.${r}`)}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fFile} onValueChange={setFFile}>
          <SelectTrigger className="h-8 w-auto min-w-[130px] text-xs" data-testid="filter-source-file"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>{t('planner.archive.sourceFile')}: 全て</SelectItem>{sourceFiles.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
        </Select>
        {selectedIds.length > 0 && (
          <Button size="sm" onClick={bulkApprove} data-testid="archive-bulk-approve-btn" className="bg-teal-700 hover:bg-teal-800 h-8">
            <Check size={14} className="mr-1" /> {t('planner.archive.bulkApprove')} ({selectedIds.length})
          </Button>
        )}
      </div>

      <div className="space-y-2" data-testid="archive-review-list">
        {records.length === 0 && <p className="text-sm text-zinc-400 text-center py-10" data-testid="archive-review-empty">{t('planner.archive.noRecords')}</p>}
        {records.map((rec) => (
          <div key={rec.id} className="flex items-start gap-2">
            {rec.match_confidence === 'high' && rec.review_status === 'pending_review' && (
              <input type="checkbox" className="mt-4" checked={!!selected[rec.id]} data-testid={`archive-select-${rec.id}`}
                onChange={(e) => setSelected((s) => ({ ...s, [rec.id]: e.target.checked }))} />
            )}
            <div className="flex-1 min-w-0">
              <ArchivedContextCard rec={rec} t={t} showStatus />
              <div className="flex flex-wrap items-center gap-2 mt-1 pl-1">
                <span className="text-[11px] text-zinc-500">{t('planner.archive.candidate')}: <b className="text-zinc-700">{rec.candidate_project_name || '—'}</b></span>
                {rec.reviewer_note && <span className="text-[11px] text-sky-600">{t('planner.archive.reviewerNote')}: {rec.reviewer_note}</span>}
                {rec.old_project_id && (
                  <Link to={`/planner/old-projects?open=${rec.old_project_id}`} data-testid={`archive-oldproj-vault-link-${rec.id}`}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-violet-600 hover:text-violet-700">
                    <Vault size={12} weight="duotone" /> {t('planner.oldproj.viewInVault')}
                  </Link>
                )}
                <div className="ml-auto flex flex-wrap gap-1.5">
                  {rec.review_status !== 'approved_link' && rec.review_status !== 'old_project_created' && (
                    <Button size="sm" variant="outline" onClick={() => doApprove(rec)} disabled={!rec.candidate_current_project_id} data-testid={`archive-approve-${rec.id}`}
                      className="h-7 text-emerald-700 border-emerald-200 hover:bg-emerald-50 text-xs"><Check size={13} className="mr-0.5" /> {t('planner.archive.approve')}</Button>
                  )}
                  {rec.review_status !== 'rejected_link' && rec.review_status !== 'old_project_created' && (
                    <Button size="sm" variant="outline" onClick={() => doReject(rec)} data-testid={`archive-reject-${rec.id}`}
                      className="h-7 text-rose-600 border-rose-200 hover:bg-rose-50 text-xs"><X size={13} className="mr-0.5" /> {t('planner.archive.reject')}</Button>
                  )}
                  {canCreateOld(rec) && (
                    <Button size="sm" variant="outline" onClick={() => setOldProjRec(rec)} data-testid={`archive-create-oldproj-${rec.id}`}
                      className="h-7 text-violet-700 border-violet-200 hover:bg-violet-50 text-xs"><Vault size={13} className="mr-0.5" /> {t('planner.oldproj.createShort')}</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEdit(rec)} data-testid={`archive-edit-${rec.id}`} className="h-7 text-xs">
                    <PencilSimple size={13} className="mr-0.5" /> {t('planner.edit')}</Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editRec} onOpenChange={(o) => !o && setEditRec(null)}>
        <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="archive-edit-dialog">
          <DialogHeader><DialogTitle>{t('planner.archive.editTitle')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-zinc-600">{t('planner.archive.candidate')}</label>
              <Select value={editCand} onValueChange={setEditCand}>
                <SelectTrigger className="h-9 mt-1" data-testid="archive-edit-candidate-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>（なし）</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-zinc-600">{t('planner.archive.relationshipType')}</label>
              <Select value={editRel} onValueChange={setEditRel}>
                <SelectTrigger className="h-9 mt-1" data-testid="archive-edit-relationship-select"><SelectValue /></SelectTrigger>
                <SelectContent>{RELS.map((r) => <SelectItem key={r} value={r}>{t(`planner.archive.rel.${r}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-zinc-600">{t('planner.archive.reviewerNote')}</label>
              <Textarea value={editNote} onChange={(e) => setEditNote(e.target.value)} rows={2} className="mt-1 text-sm" data-testid="archive-edit-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRec(null)}>{t('planner.cancel')}</Button>
            <Button onClick={saveEdit} data-testid="archive-edit-save-btn" className="bg-teal-700 hover:bg-teal-800">{t('planner.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateOldProjectDialog rec={oldProjRec} onClose={() => setOldProjRec(null)}
        onCreated={() => { setOldProjRec(null); load(); }} />
    </div>
  );
}
